// src/pages/InterviewResults.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/InterviewResults.css";
import Navbar from "../components/Navbar";
import "bootstrap-icons/font/bootstrap-icons.css";
import logoMirai from "../assets/mirai_logo.png";
import { PLATFORM_PDF_FOOTER } from '../constants/platform';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  PDFDownloadLink,
} from "@react-pdf/renderer";

// === Estilos PDF ===
const pdfStyles = StyleSheet.create({
  page: { padding: 40, fontSize: 12, fontFamily: "Helvetica" },
  header: { textAlign: "center", marginBottom: 20 },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#004d40",
    marginTop: 10,
  },
  logo: { width: 60, height: 60, margin: "0 auto" },
  studentInfo: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 8,
    color: "#333",
  },
  card: {
    border: "1pt solid #ccc",
    borderRadius: 6,
    padding: 8,
    marginBottom: 10,
    backgroundColor: "#fff",
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    fontSize: 9,
    textAlign: "center",
    color: "#666",
  },
  txtJustify: { fontSize: 11, textAlign: "justify" },
});

// === Documento PDF ===
const InterviewPDF = ({ user, data }) => {
  if (!data) return <Document />;

  const date = new Date().toLocaleDateString();

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <Image
          src={logoMirai}
          style={{
            position: "absolute",
            top: "30%",
            left: "20%",
            width: 300,
            height: 300,
            opacity: 0.1,
          }}
        />

        <View style={pdfStyles.header}>
          <Image src={logoMirai} style={pdfStyles.logo} />
          <Text style={pdfStyles.title}>Resultados de Entrevista</Text>
        </View>

        <View style={pdfStyles.studentInfo}>
          <Text>Nombre del estudiante: {user?.name || "N/D"}</Text>
          <Text>Fecha: {date}</Text>
          <Text>Puntaje total: {data?.score ?? "N/D"}/100</Text>
        </View>

        <Text style={pdfStyles.sectionTitle}>Análisis Detallado</Text>
        {data?.analysis?.length
          ? data.analysis.map((item, i) => (
            <View key={i} style={pdfStyles.card} wrap={false}>
              <View style={pdfStyles.cardHeader}>
                <Text>Criterio {i + 1}</Text>
                <Text>Puntaje: {item?.score ?? "N/D"}</Text>
              </View>
              <Text style={pdfStyles.txtJustify}>
                {item?.explanation || "Sin explicación"}
              </Text>
            </View>
          ))
          : (
            <View style={pdfStyles.card} wrap={false}>
              <Text>No hay análisis disponible.</Text>
            </View>
          )
        }

        <Text style={pdfStyles.sectionTitle}>Preguntas y Respuestas</Text>
        {data?.questions?.length
          ? data.questions.map((q, i) => (
            <View key={i} style={pdfStyles.card} wrap={false}>
              <Text style={{ fontWeight: "bold" }}>
                P{i + 1}: {q}
              </Text>
              <Text>Respuesta: {data?.responses?.[i] || "N/D"}</Text>
            </View>
          ))
          : (
            <View style={pdfStyles.card} wrap={false}>
              <Text>No hay preguntas/respuestas disponibles.</Text>
            </View>
          )
        }

        <Text style={pdfStyles.footer}>
          {PLATFORM_PDF_FOOTER}
        </Text>
      </Page>
    </Document>
  );
};

const InterviewResults = () => {
  const [interviewData, setInterviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState("overview"); // Used for Sidebar highlighting
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        const userRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/me`,
          { headers: { Authorization: token } }
        );
        setUser(userRes.data);

        const interviewRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/interview-responses`,
          { headers: { Authorization: token } }
        );
        setInterviewData(interviewRes.data);
      } catch (err) {
        setError("No se encontraron respuestas de entrevista.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getScoreBadgeClass = (score) => {
    if (score >= 80) return "high";
    if (score >= 60) return "med";
    return "low";
  };

  return (
    <>
      <Navbar />

      <div className="dashboard-layout">
        {/* Sidebar Standard */}
        <div className="dashboard-sidebar">
          <div className="sidebar-header">
            <h3>Entrevista</h3>
          </div>

          <div className="sidebar-menu">
            <button
              className={`menu-item ${activeSection === "overview" ? "active" : ""}`}
              onClick={() => scrollToSection("overview")}
            >
              <i className="bi bi-speedometer2"></i> Resumen
            </button>

            <button
              className={`menu-item ${activeSection === "analysis" ? "active" : ""}`}
              onClick={() => scrollToSection("analysis")}
            >
              <i className="bi bi-graph-up"></i> Análisis
            </button>

            <button
              className={`menu-item ${activeSection === "questions" ? "active" : ""}`}
              onClick={() => scrollToSection("questions")}
            >
              <i className="bi bi-chat-dots"></i> Preguntas
            </button>

            <button
              className="menu-item return-dashboard"
              onClick={() => navigate("/dashboard")}
            >
              <i className="bi bi-arrow-left-circle"></i> Volver al Dashboard
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          <div className="ir-container">
            {loading ? (
              <div className="ir-loading">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p>Procesando resultados de entrevista...</p>
              </div>
            ) : error ? (
              <div className="ir-error">
                <i className="bi bi-exclamation-triangle ir-icon-placeholder"></i>
                <h3>Error</h3>
                <p>{error}</p>
                <button className="btn btn-primary mt-3" onClick={() => navigate("/dashboard")}>
                  Volver al Dashboard
                </button>
              </div>
            ) : !interviewData ? (
              <div className="ir-empty">
                <i className="bi bi-file-earmark-x ir-icon-placeholder"></i>
                <h3>Sin resultados</h3>
                <p>No se encontraron datos de entrevista para este usuario.</p>
              </div>
            ) : (
              <>
                <div id="overview" className="ir-header">
                  <h2><i className="bi bi-person-video"></i> Resultados de Entrevista Inteligente</h2>
                  <p>Informe detallado de análisis y respuestas</p>
                </div>

                {/* Stats Grid */}
                <div className="ir-stats-grid">
                  <div className="ir-stat-card primary-card">
                    <div className="icon-wrapper">
                      <i className="bi bi-trophy"></i>
                    </div>
                    <div className="content-wrapper">
                      <h3>{interviewData?.score ?? 0}<span className="small-text">/100</span></h3>
                      <p>Puntaje Global</p>
                    </div>
                  </div>

                  <div className="ir-stat-card info-card">
                    <div className="icon-wrapper">
                      <i className="bi bi-chat-quote"></i>
                    </div>
                    <div className="content-wrapper">
                      <h3>{interviewData?.questions?.length ?? 0}</h3>
                      <p>Preguntas Evaluadas</p>
                    </div>
                  </div>

                  <div className="ir-stat-card success-card">
                    <div className="icon-wrapper">
                      <i className="bi bi-clipboard-check"></i>
                    </div>
                    <div className="content-wrapper">
                      <h3>{interviewData?.analysis?.length ?? 0}</h3>
                      <p>Criterios Analizados</p>
                    </div>
                  </div>
                </div>

                {/* Analysis Section */}
                <div id="analysis" className="ir-section">
                  <div className="ir-section-title">
                    <i className="bi bi-graph-up"></i>
                    <span>Desglose de Análisis por Criterio</span>
                  </div>

                  <div className="ir-analysis-grid">
                    {interviewData?.analysis?.map((item, index) => (
                      <div key={index} className="ir-analysis-card">
                        <div className="ir-analysis-header">
                          <h4>Criterio #{index + 1}</h4>
                          <span className={`score-badge ${getScoreBadgeClass(item?.score)}`}>
                            {item?.score ?? 0} pts
                          </span>
                        </div>
                        <div className="ir-analysis-content">
                          <p>{item?.explanation || "Sin explicación disponible."}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Q&A Section */}
                <div id="questions" className="ir-section">
                  <div className="ir-section-title">
                    <i className="bi bi-chat-left-text"></i>
                    <span>Registro de Preguntas y Respuestas</span>
                  </div>

                  <div className="ir-qa-list">
                    {interviewData?.questions?.map((question, index) => (
                      <div key={index} className="ir-qa-card">
                        <div className="ir-question">
                          <span className="q-badge">P{index + 1}</span>
                          <p className="q-text">{question}</p>
                        </div>
                        <div className="ir-answer">
                          <span className="a-badge">R</span>
                          <p className="a-text">{interviewData?.responses?.[index] || "Sin respuesta registrada."}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer Action */}
                <div className="ir-footer">
                  <PDFDownloadLink
                    document={<InterviewPDF user={user} data={interviewData} />}
                    fileName={`Resultados_Entrevista_${new Date()
                      .toLocaleDateString()
                      .replaceAll("/", "_")}.pdf`}
                    className="text-decoration-none"
                  >
                    {({ loading }) => (
                      <button className="btn btn-primary ir-btn" disabled={loading}>
                        <i className={`bi ${loading ? 'bi-hourglass-split' : 'bi-file-earmark-pdf'}`}></i>
                        {loading ? ' Generando Reporte PDF...' : ' Descargar Informe Completo'}
                      </button>
                    )}
                  </PDFDownloadLink>
                </div>

              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InterviewResults;
